import { useMemo } from 'react';

import { Nomenclator } from '@/api/Nomenclator';
import { Services } from '@/api/Services';
import { Shipping } from '@/api/Shipping';
import {
  CountyView,
  DeliveryAddressRequest,
  DeliveryAddressResponse,
  DeliveryAddressSearchResponse,
  FanStreetData,
  GenerateAwbRequest,
  GenerateAwbResponse,
  LocalityView,
  PageResponsePickupOrderResponse,
  PageResponseShipmentResponse,
  PickupOrderResponse,
  PickupOrderSearchRequest,
  PickupPointView,
  PlacePickupOrderRequest,
  ServiceOptionView,
  ServiceView,
  ShipmentResponse,
  ShipmentSearchRequest,
  ShipmentTrackingEventResponse,
  TariffPreviewRequest,
  TariffQuote,
} from '@/api/data-contracts';
import { RequestParams } from '@/api/http-client';
import { optionalApiMethod } from '@/shared/api';
import useUploadProgressService from '@/shared/api/uploadProgress.service';

type ListServiceOptions = (
  query: { service: string; clientId?: string },
  params?: RequestParams,
) => Promise<{ data: ServiceOptionView[] }>;

export interface DeliveryAddressSearchParams {
  kind?: DeliveryAddressRequest['kind'];
  supplierId?: string;
  search?: string;
  serviceTaskId?: string;
  includeArchived?: boolean;
  page?: number;
  size?: number;
}

const DELIVERY_ADDRESS_SEARCH_DEFAULTS = {
  includeArchived: false,
  page: 0,
  size: 200,
};

export interface ShippingService {
  listCounties: (signal?: AbortSignal) => Promise<CountyView[]>;
  listLocalities: (
    county: string,
    search?: string,
    signal?: AbortSignal,
  ) => Promise<LocalityView[]>;
  listStreets: (
    county: string,
    locality: string,
    search?: string,
    signal?: AbortSignal,
  ) => Promise<FanStreetData[]>;
  listCourierServices: (signal?: AbortSignal) => Promise<ServiceView[]>;
  listServiceOptions: (
    service: string,
    clientId: string | undefined,
    signal?: AbortSignal,
  ) => Promise<ServiceOptionView[]>;
  listPickupPoints: (signal?: AbortSignal) => Promise<PickupPointView[]>;
  searchDeliveryAddresses: (
    params: DeliveryAddressSearchParams,
    signal?: AbortSignal,
  ) => Promise<DeliveryAddressSearchResponse>;
  createDeliveryAddress: (data: DeliveryAddressRequest) => Promise<DeliveryAddressResponse>;
  updateDeliveryAddress: (
    id: string,
    data: DeliveryAddressRequest,
  ) => Promise<DeliveryAddressResponse>;
  listTaskShipments: (taskId: string, signal?: AbortSignal) => Promise<ShipmentResponse[]>;
  getShipment: (id: string, signal?: AbortSignal) => Promise<ShipmentResponse>;
  searchShipments: (
    request: ShipmentSearchRequest,
    signal?: AbortSignal,
  ) => Promise<PageResponseShipmentResponse>;
  getShipmentTracking: (
    id: string,
    signal?: AbortSignal,
  ) => Promise<ShipmentTrackingEventResponse[]>;
  getShipmentLabel: (id: string) => Promise<Blob>;
  voidShipment: (id: string, reason?: string) => Promise<ShipmentResponse>;
  generateAwb: (taskId: string, data: GenerateAwbRequest) => Promise<GenerateAwbResponse>;
  previewShipmentTariff: (data: TariffPreviewRequest, signal?: AbortSignal) => Promise<TariffQuote>;
  listPendingPickups: (signal?: AbortSignal) => Promise<PickupOrderResponse[]>;
  searchPickupOrders: (
    request: PickupOrderSearchRequest,
    signal?: AbortSignal,
  ) => Promise<PageResponsePickupOrderResponse>;
  placePickupOrder: (data: PlacePickupOrderRequest) => Promise<PickupOrderResponse>;
}

export const useShippingService = (): ShippingService => {
  const shippingApi: Shipping = useUploadProgressService(Shipping);
  const nomenclatorApi: Nomenclator = useUploadProgressService(Nomenclator);
  const servicesApi: Services = useUploadProgressService(Services);

  return useMemo(
    () => ({
      listCounties: (signal) => nomenclatorApi.listCounties({ signal }).then((r) => r.data),
      listLocalities: (county, search, signal) =>
        nomenclatorApi.listLocalities({ county, search }, { signal }).then((r) => r.data),
      listStreets: (county, locality, search, signal) =>
        nomenclatorApi.listStreets({ county, locality, search }, { signal }).then((r) => r.data),
      listCourierServices: (signal) =>
        nomenclatorApi.listCourierServices({ signal }).then((r) => r.data),
      listServiceOptions: (service, clientId, signal) => {
        const call = optionalApiMethod<ListServiceOptions>(nomenclatorApi, 'listServiceOptions');
        if (!call) return Promise.resolve([]);
        return call({ service, clientId }, { signal }).then((r) => r.data);
      },
      listPickupPoints: (signal) => shippingApi.listPickupPoints({ signal }).then((r) => r.data),
      searchDeliveryAddresses: (params, signal) =>
        shippingApi
          .searchDeliveryAddresses({ ...DELIVERY_ADDRESS_SEARCH_DEFAULTS, ...params }, { signal })
          .then((r) => r.data),
      createDeliveryAddress: (data) => shippingApi.createDeliveryAddress(data).then((r) => r.data),
      updateDeliveryAddress: (id, data) =>
        shippingApi.updateDeliveryAddress(id, data).then((r) => r.data),
      listTaskShipments: (taskId, signal) =>
        servicesApi.listTaskShipments(taskId, { signal }).then((r) => r.data),
      getShipment: (id, signal) => shippingApi.getShipment(id, { signal }).then((r) => r.data),
      searchShipments: (request, signal) =>
        shippingApi.searchShipments(request, { signal }).then((r) => r.data),
      getShipmentTracking: (id, signal) =>
        shippingApi.getShipmentTracking(id, { signal }).then((r) => r.data),
      voidShipment: (id, reason) => shippingApi.voidShipment(id, { reason }).then((r) => r.data),
      getShipmentLabel: (id) =>
        shippingApi
          .getShipmentLabel(id, { format: 'blob' } as RequestParams)
          .then((r) => r.data as unknown as Blob),
      generateAwb: (taskId, data) => servicesApi.generateAwb(taskId, data).then((r) => r.data),
      previewShipmentTariff: (data, signal) =>
        shippingApi.previewShipmentTariff(data, { signal }).then((r) => r.data),
      listPendingPickups: (signal) =>
        shippingApi.listPendingPickups({ signal }).then((r) => r.data),
      searchPickupOrders: (request, signal) =>
        shippingApi.searchPickupOrders(request, { signal }).then((r) => r.data),
      placePickupOrder: (data) => shippingApi.placePickupOrder(data).then((r) => r.data),
    }),
    [nomenclatorApi, servicesApi, shippingApi],
  );
};
